import find from 'lodash/find';
import throttle from 'lodash/throttle';
import React, {Component, createRef} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import loadable from '@loadable/component';
import Select from '@spothero/ui/select';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import {SEARCH_TYPE} from '../search-prop-types';
import SpotListSearchControls from './SpotListSearchControls';
import SpotListSearchControlsCondensed from './SpotListSearchControlsCondensed';
import SpotList from './SpotList';
import SpotListDestinationInfo from './SpotListDestinationInfo';

const EventList = loadable(() => import('event/EventList'));

export default class SpotListContainer extends Component {
    static propTypes = {
        isMobile: PropTypes.bool,
        searchType: SEARCH_TYPE.isRequired,
        sortBy: PropTypes.string,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        city: PropTypes.object,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        event: PropTypes.object,
        spots: PropTypes.arrayOf(PropTypes.object).isRequired,
        showListLoader: PropTypes.bool,
        showLoadingComplete: PropTypes.bool,
        showDestinationInfo: PropTypes.bool,
        isEventListEnabled: PropTypes.bool,
        showEventList: PropTypes.bool,
        onToggleEventList: PropTypes.func.isRequired,
        onToggleInfoModal: PropTypes.func,
        loadNextPage: PropTypes.func,
        isLowInventoryEnabled: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        onChangeSort: PropTypes.func,
        onShowDetailsClick: PropTypes.func,
    };
    state = {
        showCondensedControls: false,
    };
    _listRef = createRef();
    _sortSelectItems = [
        {value: 'distance', label: 'Sort by Distance'},
        {value: 'price', label: 'Sort by Price'},
    ];

    componentDidMount() {
        const {spots} = this.props;

        this._searchControlsEl = DOMUtils.el('.SpotListSearchControls');
        this._activeSpot = find(spots, 'activeMarker');
        this._onListThrottle = throttle(this._onListScroll, 500);
    }

    componentDidUpdate(prevProps, prevState) {
        const {isMobile, spots} = this.props;

        if (!isMobile) {
            const newActiveSpot = find(spots, 'activeMarker');

            if (
                newActiveSpot &&
                (!this._activeSpot ||
                    this._activeSpot.spotId !== newActiveSpot.spotId)
            ) {
                // if a new active spot is set, scroll the list to its position
                const item = DOMUtils.el(`.result${newActiveSpot.spotId}`);

                if (item) {
                    AnimationUtils.scrollTo(
                        this._listRef.current,
                        item.offsetTop,
                        250,
                        'y',
                        () => {
                            DOMUtils.removeClass(item, 'highlight'); // remove it first in case it existed

                            // a short delay ensures the css animation repeats
                            setTimeout(() => {
                                DOMUtils.addClass(item, 'highlight');
                            }, 150);
                        }
                    );
                }
            }

            this._activeSpot = newActiveSpot;
        }
    }

    componentWillUnmount() {
        if (this._onListThrottle) {
            this._onListThrottle.cancel();
        }
    }

    _onListScroll = () => {
        this.setState({
            showCondensedControls:
                this._listRef.current.scrollTop >
                this._searchControlsEl.offsetHeight,
        });
    };

    _onEditClick = evt => {
        AnimationUtils.scrollTo(this._listRef.current, 0);
    };

    _onSortChange = ({value}) => {
        this.props.onChangeSort(value);
    };

    render() {
        const {
            isMobile,
            searchType,
            sortBy,
            isMonthly,
            starts,
            ends,
            activeSearchTitle,
            city,
            destination,
            event,
            spots,
            showDestinationInfo,
            showListLoader,
            showLoadingComplete,
            isEventListEnabled,
            showEventList,
            onToggleEventList,
            onToggleInfoModal,
            loadNextPage,
            isLowInventoryEnabled,
            isSoldOutEnabled,
            onShowDetailsClick,
        } = this.props;
        const {showCondensedControls} = this.state;
        const isEvent = searchType === 'event';
        const classes = classNames({
            'SpotListContainer-show-condensed-controls': showCondensedControls,
        });

        return (
            <div className={classes}>
                <div
                    ref={this._listRef}
                    className="SpotListContainer"
                    itemScope={isEvent ? true : null}
                    itemType={isEvent ? 'http://schema.org/Event' : null}
                    onScroll={this._onListThrottle}
                >
                    <SpotListSearchControls
                        hideInfoToggle={showDestinationInfo}
                        numSpots={spots.length}
                        onToggleInfoModal={onToggleInfoModal}
                        onToggleEventList={onToggleEventList}
                    />
                    {!isMobile && spots.length > 0 && (
                        <div className="SpotList-sort">
                            <Select
                                defaultValue={sortBy}
                                onChange={this._onSortChange}
                                items={this._sortSelectItems}
                            />
                        </div>
                    )}
                    <SpotList
                        spots={spots}
                        showListLoader={showListLoader}
                        showLoadingComplete={showLoadingComplete}
                        isEventListEnabled={isEventListEnabled}
                        onToggleEventList={onToggleEventList}
                        loadNextPage={loadNextPage}
                        isLowInventoryEnabled={isLowInventoryEnabled}
                        isSoldOutEnabled={isSoldOutEnabled}
                        onShowDetailsClick={onShowDetailsClick}
                    />
                    {showDestinationInfo && (
                        <SpotListDestinationInfo
                            isMonthly={isMonthly}
                            city={city}
                            destination={destination}
                            event={event}
                        />
                    )}
                </div>
                <SpotListSearchControlsCondensed
                    starts={starts}
                    ends={ends}
                    isMonthly={isMonthly}
                    title={activeSearchTitle}
                    onEditClick={this._onEditClick}
                />
                {!isMobile && isEventListEnabled && (
                    <EventList
                        showEventList={showEventList}
                        onToggleEventList={onToggleEventList}
                    />
                )}
            </div>
        );
    }
}
